import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress, Alert, Tooltip, Snackbar } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CancelIcon from '@mui/icons-material/Cancel';
import { getAssinaturas, cancelarAssinatura, AssinaturaDigital } from '../services/api';

const AssinaturasPendentesPage: React.FC = () => {
    const [assinaturas, setAssinaturas] = useState<AssinaturaDigital[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean, message: string }>({ open: false, message: '' });

    const fetchPendentes = async () => {
        try {
            setLoading(true);
            const data = await getAssinaturas('PENDENTE');
            setAssinaturas(data);
        } catch (err: any) {
            setError(err.message || 'Erro ao carregar assinaturas pendentes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPendentes();
    }, []);

    const handleCopyLink = (token: string) => {
        const url = `${window.location.origin}/assinatura/${token}`;
        navigator.clipboard.writeText(url);
        setSnackbar({ open: true, message: 'Link copiado para a área de transferência!' });
    };

    const handleCancel = async (id: string) => {
        if (!window.confirm('Tem certeza que deseja cancelar esta solicitação de assinatura?')) return;
        try {
            await cancelarAssinatura(id);
            setSnackbar({ open: true, message: 'Assinatura cancelada com sucesso.' });
            fetchPendentes();
        } catch (err: any) {
            alert('Erro ao cancelar: ' + err.message);
        }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Typography variant="h4" gutterBottom>Assinaturas Pendentes</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Data da Solicitação</TableCell>
                                <TableCell>Obra</TableCell>
                                <TableCell>Fornecedor</TableCell>
                                <TableCell>Enviado Para</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {assinaturas.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center">Nenhuma assinatura pendente no momento.</TableCell></TableRow>
                            ) : assinaturas.map((ass) => {
                                const lanc = ass.lancamentos_financeiros;
                                return (
                                    <TableRow key={ass.id}>
                                        <TableCell>{new Date(ass.created_at).toLocaleDateString('pt-BR')}</TableCell>
                                        <TableCell>{lanc?.obras?.nome || '-'}</TableCell>
                                        <TableCell>{ass.nome_assinante}</TableCell>
                                        <TableCell>
                                            {ass.metodo_envio === 'sms' && ass.telefone_assinante ? ass.telefone_assinante : (ass.metodo_envio === 'ambos' ? `${ass.email_assinante} / ${ass.telefone_assinante}` : ass.email_assinante)}
                                        </TableCell>
                                        <TableCell>
                                            {lanc?.valor_total_liquido ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(lanc.valor_total_liquido) : '-'}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip label="Pendente" color="warning" size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Copiar Link de Assinatura">
                                                <IconButton color="primary" onClick={() => handleCopyLink(ass.token_acesso)}>
                                                    <ContentCopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Cancelar Solicitação">
                                                <IconButton color="error" onClick={() => handleCancel(ass.id)}>
                                                    <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
            />
        </Box>
    );
};

export default AssinaturasPendentesPage;
